import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools, faCalendar, faPaperPlane, faChevronDown, faImage, faTimes, faSpinner, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Translation from '../components/common/Translation';
import axios from 'axios';
import '../styles/maintenance-request.css';

const MaintenanceRequestPage = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    maintenanceType: '',
    description: '',
    preferredDate: '',
    quality: 'normal',
    images: []
  });

  const [imagePreview, setImagePreview] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    message: '',
    type: ''
  });

  // Effect to handle RTL/LTR document direction based on language
  useEffect(() => {
    // Set document direction
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
  }, [language]);

  const maintenanceTypes = [
    { key: 'electrical', label: 'electrical' },
    { key: 'plumbing', label: 'plumbing' },
    { key: 'airConditioning', label: 'airConditioning' },
    { key: 'heating', label: 'heating' },
    { key: 'applianceRepair', label: 'applianceRepair' },
    { key: 'structural', label: 'structural' },
    { key: 'pestControl', label: 'pestControl' },
    { key: 'other', label: 'other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ message: '', type: '' });

    try {
      // Create FormData object to handle file uploads
      const formDataToSend = new FormData();
      formDataToSend.append('maintenanceType', formData.maintenanceType);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('preferredDate', formData.preferredDate);
      formDataToSend.append('quality', formData.quality);
      
      // Append each image file
      formData.images.forEach((image, index) => {
        formDataToSend.append(`images[${index}]`, image);
      });

      // Determine the endpoint based on authentication status
      const endpoint = isAuthenticated 
        ? 'https://realestate.bonlineco.com/api/user/maintenance-request'
        : 'https://realestate.bonlineco.com/api/maintenance-request';

      const response = await axios.post(endpoint, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Language': language, // Send current language preference
          ...(isAuthenticated && {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          })
        }
      });

      if (response.data.status === 'success') {
        setSubmitStatus({
          message: language === 'ar' ? 'تم إرسال طلب الصيانة بنجاح!' : 'Maintenance request submitted successfully!',
          type: 'success'
        });
        
        // Reset form
        setFormData({
          maintenanceType: '',
          description: '',
          preferredDate: '',
          quality: 'normal',
          images: []
        });
        setImagePreview([]);
      }
    } catch (error) {
      console.error('Error submitting maintenance request:', error);
      setSubmitStatus({
        message: error.response?.data?.message || 
                (language === 'ar' ? 'فشل في إرسال الطلب. يرجى المحاولة مرة أخرى.' : 'Failed to submit request. Please try again.'),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
      // Scroll to top to show the status message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = [...formData.images];
    const newPreviews = [...imagePreview];

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        newImages.push(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          setImagePreview([...newPreviews]);
        };
        reader.readAsDataURL(file);
      }
    });

    setFormData(prevState => ({
      ...prevState,
      images: newImages
    }));
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const newPreviews = imagePreview.filter((_, i) => i !== index);
    
    setFormData(prevState => ({
      ...prevState,
      images: newImages
    }));
    setImagePreview(newPreviews);
  };

  return (
    <div className="maintenance-request-page">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="maintenance-form-container">
              <div className="text-center mb-4">
                <FontAwesomeIcon icon={faTools} className="maintenance-icon" />
                <h2><Translation textKey="maintenanceRequestForm" /></h2>
                <p className="text-muted"><Translation textKey="maintenanceRequestSubtitle" /></p>
                
                {submitStatus.message && (
                  <div className={`alert alert-${submitStatus.type === 'success' ? 'success' : 'danger'} mt-3`}>
                    <FontAwesomeIcon 
                      icon={submitStatus.type === 'success' ? faCheckCircle : faExclamationTriangle} 
                      className="me-2" 
                    />
                    {submitStatus.message}
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="maintenance-form">
                <div className="form-group mb-4">
                  <label htmlFor="maintenanceType"><Translation textKey="maintenanceType" /></label>
                  <div className="select-wrapper">
                    <select
                      id="maintenanceType"
                      name="maintenanceType"
                      className="form-control"
                      value={formData.maintenanceType}
                      onChange={handleChange}
                      required
                    >
                      <option value=""><Translation textKey="selectMaintenanceType" /></option>
                      {maintenanceTypes.map(type => (
                        <option key={type.key} value={type.key}>
                          <Translation textKey={type.label} />
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon icon={faChevronDown} className="select-arrow" />
                  </div>
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="description"><Translation textKey="problemDescription" /></label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control"
                    rows="4"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder={language === 'ar' ? 'يرجى وصف المشكلة بالتفصيل...' : 'Please describe the issue in detail...'}
                  ></textarea>
                </div>

                <div className="form-group mb-4">
                  <label><Translation textKey="problemQuality" /></label>
                  <div className="quality-options">
                    <div className="form-check">
                      <input
                        type="radio"
                        id="urgent"
                        name="quality"
                        value="urgent"
                        className="form-check-input"
                        checked={formData.quality === 'urgent'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="urgent"><Translation textKey="urgent" /></label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="normal"
                        name="quality"
                        value="normal"
                        className="form-check-input"
                        checked={formData.quality === 'normal'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="normal"><Translation textKey="normal" /></label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        id="low"
                        name="quality"
                        value="low"
                        className="form-check-input"
                        checked={formData.quality === 'low'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="low"><Translation textKey="lowPriority" /></label>
                    </div>
                  </div>
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="preferredDate">
                    <FontAwesomeIcon icon={faCalendar} className="me-2" />
                    <Translation textKey="preferredVisitDate" />
                  </label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    className="form-control"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group mb-4">
                  <label>
                    <FontAwesomeIcon icon={faImage} className="me-2" />
                    <Translation textKey="uploadImages" />
                  </label>
                  <div className="image-upload-container">
                    <div className="custom-file-input-wrapper">
                      <input
                        type="file"
                        id="images"
                        name="images"
                        onChange={handleImageUpload}
                        accept="image/*"
                        multiple
                      />
                      <label htmlFor="images" className="file-input-container">
                        <span className="file-input-button">
                          {language === 'ar' ? 'اختيار الصور' : 'Choose Images'}
                        </span>
                        <span className="file-input-text">
                          {formData.images.length === 0 
                            ? (language === 'ar' ? 'لم يتم اختيار ملف' : 'No file chosen')
                            : `${formData.images.length} ${language === 'ar' ? 'ملفات مختارة' : 'files selected'}`
                          }
                        </span>
                      </label>
                    </div>
                    {imagePreview.length > 0 && (
                      <div className="image-preview-grid">
                        {imagePreview.map((preview, index) => (
                          <div key={index} className="image-preview-item">
                            <img src={preview} alt={`Preview ${index + 1}`} />
                            <button
                              type="button"
                              className="remove-image"
                              onClick={() => removeImage(index)}
                              aria-label="Remove image"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      <span>{language === 'ar' ? 'جاري الإرسال...' : 'Submitting...'}</span>
                    </>
                  ) : (
                    <>
                      <span><Translation textKey="submitRequest" /></span>
                      <FontAwesomeIcon icon={faPaperPlane} className="ms-2" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceRequestPage;
